import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Icon } from '../components/ui/Icon.js';

interface StepIngredient {
  recipeIngredientId: string;
  ingredientName: string;
  quantityText: string | null;
  unitText: string | null;
  rawText: string;
}

interface InstructionStep {
  id: string;
  order: number;
  text: string;
  durationSeconds: number | null;
  ingredients: StepIngredient[];
}

interface RecipeDetailData {
  id: string;
  name: string;
  mealType: string;
  instructions: InstructionStep[];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${hours} hr` : `${hours} hr ${rem} min`;
}

function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const anyNav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!anyNav.wakeLock) return;
    let sentinel: { release: () => Promise<void> } | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const s = await anyNav.wakeLock!.request('screen');
        if (cancelled) {
          await s.release().catch(() => {});
          return;
        }
        sentinel = s;
      } catch {
        // Permission denied or device unsupported — silent fall-through
      }
    };
    acquire();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !sentinel) acquire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release().catch(() => {});
    };
  }, [active]);
}

export function CookingModePage() {
  const { id, stepIndex } = useParams<{ id: string; stepIndex: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const idx = Number(stepIndex ?? '0');
  const [sheetOpen, setSheetOpen] = useState(false);
  const advanceLockRef = useRef(false);

  const exit = useCallback(() => {
    if (returnTo) {
      navigate(returnTo, { replace: true, state: { reopenRecipeId: id } });
    } else {
      navigate(-1);
    }
  }, [navigate, returnTo, id]);

  const { data: recipe, isLoading, isError } = useQuery<RecipeDetailData>({
    queryKey: ['recipe', id],
    queryFn: () => api.get(`/api/recipes/${id}`),
    enabled: !!id,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const total = recipe?.instructions.length ?? 0;
  const isDone = !!recipe && idx >= total;
  const step = !isDone && recipe ? recipe.instructions[idx] : null;

  useWakeLock(!!recipe && !isError);

  const goTo = useCallback(
    (next: number) => {
      if (!id) return;
      if (next < 0) return;
      // Carry `returnTo` forward on every step transition so `exit()` can use
      // it from any step, not just step 0 where RecipeModal first set it.
      navigate(`/recipes/${id}/cook/${next}`, {
        state: returnTo ? { returnTo } : undefined,
      });
    },
    [id, navigate, returnTo],
  );

  const next = useCallback(() => {
    if (advanceLockRef.current) return;
    advanceLockRef.current = true;
    setTimeout(() => { advanceLockRef.current = false; }, 300);
    goTo(idx + 1);
  }, [goTo, idx]);

  const prev = useCallback(() => {
    goTo(Math.max(0, idx - 1));
  }, [goTo, idx]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (sheetOpen) return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') { exit(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, exit, sheetOpen]);

  if (isLoading) {
    return <ShellWrap><div style={{ color: 'var(--ink-3)' }}>Loading recipe…</div></ShellWrap>;
  }

  if (isError || !recipe) {
    return (
      <ShellWrap>
        <div className="flex flex-col items-center gap-4">
          <p style={{ color: 'var(--ink-2)' }}>Recipe not found.</p>
          <button className="btn btn-soft" onClick={exit}>Back</button>
        </div>
      </ShellWrap>
    );
  }

  if (total === 0) {
    return (
      <ShellWrap>
        <div className="flex flex-col items-center gap-4 text-center">
          <p style={{ color: 'var(--ink-2)' }}>This recipe has no decomposed steps yet.</p>
          <button className="btn btn-soft" onClick={exit}>Back to recipe</button>
        </div>
      </ShellWrap>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{
        background: 'var(--bg-deep)',
        color: 'var(--ink)',
        backgroundImage: 'radial-gradient(60% 50% at 50% 0%, var(--accent-glow), transparent 70%)',
      }}
    >
      {/* Top bar */}
      <div
        className="shrink-0 flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid var(--line-soft)' }}
      >
        <button
          onClick={exit}
          aria-label="Exit cooking mode"
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          <Icon name="x" size={18} stroke={2} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--ink-4)' }}>
            Cooking
          </div>
          <div className="truncate text-sm font-medium" style={{ color: 'var(--ink-2)' }}>
            {recipe.name}
          </div>
        </div>
        <div
          className="text-xs font-mono shrink-0 px-2.5 py-1 rounded-full"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}
        >
          {isDone ? `${total}/${total}` : `${idx + 1}/${total}`}
        </div>
      </div>

      {/* Body */}
      {isDone ? (
        <DoneView onBack={exit} />
      ) : (
        <StepView step={step!} />
      )}

      {/* Bottom bar */}
      <div
        className="shrink-0 flex items-center gap-3 px-4 py-3"
        style={{ borderTop: '1px solid var(--line-soft)', background: 'color-mix(in oklab, var(--bg-deep) 85%, var(--surface))' }}
      >
        <button
          onClick={() => setSheetOpen(true)}
          aria-label="All steps"
          className="w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          <Icon name="list" size={18} stroke={2} />
        </button>

        <div className="flex-1 flex items-center justify-center gap-1.5 overflow-x-auto px-2">
          {recipe.instructions.map((_, i) => {
            const state = isDone || i < idx ? 'done' : i === idx ? 'current' : 'pending';
            return (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to step ${i + 1}`}
                className="rounded-full shrink-0 transition-all"
                style={{
                  width: state === 'current' ? 18 : 8,
                  height: 8,
                  background:
                    state === 'current' ? 'var(--accent)' :
                    state === 'done'    ? 'color-mix(in oklab, var(--accent) 55%, transparent)' :
                                          'var(--surface-3)',
                }}
              />
            );
          })}
        </div>

        <button
          onClick={prev}
          disabled={idx === 0}
          aria-label="Previous step"
          className="w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-30"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          <Icon name="chev" size={18} stroke={2} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <button
          onClick={isDone ? exit : next}
          aria-label={isDone ? 'Done' : 'Next step'}
          className="h-11 px-5 rounded-full flex items-center gap-2 font-semibold text-sm"
          style={{
            background: 'var(--accent)',
            color: 'var(--bg-deep)',
            boxShadow: '0 6px 18px -6px var(--accent-glow)',
          }}
        >
          {isDone ? 'Done' : idx === total - 1 ? 'Finish' : 'Next'}
          {!isDone && <Icon name="chev" size={16} stroke={2.4} />}
        </button>
      </div>

      {sheetOpen && (
        <StepsSheet
          recipe={recipe}
          activeIndex={idx}
          onPick={(i) => { setSheetOpen(false); goTo(i); }}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}

function ShellWrap({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{
        background: 'var(--bg-deep)',
        color: 'var(--ink)',
        backgroundImage: 'radial-gradient(60% 50% at 50% 0%, var(--accent-glow), transparent 70%)',
      }}
    >
      {children}
    </div>
  );
}

function StepView({ step }: { step: InstructionStep }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-6 pt-8 pb-12 flex flex-col gap-6">
        <div
          className="h-serif leading-none"
          style={{
            fontSize: 'clamp(72px, 12vw, 120px)',
            fontStyle: 'italic',
            color: 'var(--accent)',
          }}
        >
          {step.order + 1}
        </div>

        {step.durationSeconds != null && (
          <div
            className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-sm font-medium"
            style={{
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
            }}
          >
            <Icon name="clock" size={14} stroke={2} />
            {formatDuration(step.durationSeconds)}
          </div>
        )}

        <p
          className="font-medium"
          style={{
            fontSize: 'clamp(20px, 2.5vw, 28px)',
            lineHeight: 1.4,
            color: 'var(--ink)',
          }}
        >
          {step.text}
        </p>

        {step.ingredients.length > 0 && (
          <section>
            <div
              className="text-[10px] font-bold uppercase mb-3"
              style={{ letterSpacing: '0.16em', color: 'var(--ink-4)' }}
            >
              For this step
            </div>
            <ul className="flex flex-col gap-2">
              {step.ingredients.map((ing) => {
                const qty = ing.quantityText?.trim() ?? '';
                const unit = ing.unitText?.trim() ?? '';
                const amount = [qty, unit].filter(Boolean).join(' ');
                const name = ing.ingredientName?.trim() || ing.rawText;
                return (
                  <li
                    key={ing.recipeIngredientId}
                    className="flex items-baseline gap-3 rounded-md px-3 py-2"
                    style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
                  >
                    {amount && (
                      <span
                        className="font-mono text-sm shrink-0"
                        style={{ color: 'var(--accent)', minWidth: 80 }}
                      >
                        {amount}
                      </span>
                    )}
                    <span className="text-base" style={{ color: 'var(--ink-2)' }}>{name}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

    </div>
  );
}

function DoneView({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-6 gap-6">
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center"
        style={{
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          boxShadow: '0 10px 40px -10px var(--accent-glow)',
        }}
      >
        <Icon name="check" size={48} stroke={2.4} />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase mb-2" style={{ letterSpacing: '0.18em', color: 'var(--ink-4)' }}>
          Done cooking
        </div>
        <h2 className="h-display" style={{ fontSize: 36, color: 'var(--ink)', margin: 0 }}>
          Nice work.
        </h2>
      </div>
      <button className="btn btn-primary" onClick={onBack}>
        Back to recipe
      </button>
    </div>
  );
}

function StepsSheet({
  recipe,
  activeIndex,
  onPick,
  onClose,
}: {
  recipe: RecipeDetailData;
  activeIndex: number;
  onPick: (i: number) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-[110]"
        onClick={onClose}
        aria-hidden
        style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="All steps"
        className="fixed inset-x-0 bottom-0 z-[120] flex flex-col"
        style={{
          maxHeight: '70vh',
          background: 'var(--surface)',
          borderTop: '1px solid var(--line-soft)',
          borderTopLeftRadius: 'var(--r-lg)',
          borderTopRightRadius: 'var(--r-lg)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--line-soft)' }}>
          <div className="h-serif" style={{ fontSize: 18, color: 'var(--ink)' }}>All steps</div>
          <button
            onClick={onClose}
            aria-label="Close steps"
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            <Icon name="x" size={16} stroke={2} />
          </button>
        </div>
        <ol className="flex-1 overflow-y-auto px-2 py-2">
          {recipe.instructions.map((s, i) => {
            const isCurrent = i === activeIndex;
            return (
              <li key={s.id}>
                <button
                  onClick={() => onPick(i)}
                  className="w-full text-left flex gap-3 px-3 py-3 rounded-md transition-colors"
                  style={{
                    background: isCurrent ? 'var(--accent-soft)' : 'transparent',
                  }}
                >
                  <span
                    className="font-mono text-sm shrink-0 w-6 text-right"
                    style={{ color: isCurrent ? 'var(--accent)' : 'var(--ink-4)' }}
                  >
                    {i + 1}
                  </span>
                  <span
                    className="text-sm leading-relaxed"
                    style={{ color: isCurrent ? 'var(--ink)' : 'var(--ink-2)' }}
                  >
                    {s.text}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </>
  );
}
